import { API_URL } from "./config.ts";
import { readSessionToken, writeSessionToken } from "./platform.ts";
import { translateErrorMessage } from "./error-message.ts";
import {
  forgetAccountRole,
  normalizeAccountRole,
  rememberAccountRole,
  type AccountRole,
} from "./auth-role.ts";

/**
 * Sessions as bearer tokens.
 *
 * The SPA and the API sit on different origins, so a cookie session would need
 * third-party-cookie tolerance we can't count on. Better Auth's `bearer()`
 * plugin returns the session token on sign-in; we keep it in localStorage and
 * send it as an Authorization header.
 */

const USER_KEY = "dd-user";

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  twoFactorEnabled?: boolean;
  /**
   * Si el correo de la cuenta está probado.
   *
   * El portal lo necesita: el enganche con la ficha del consultorio sólo mira
   * correos verificados, así que sin esto la pantalla no puede distinguir "no
   * tenés ficha" de "todavía no probamos que este correo es tuyo".
   */
  emailVerified?: boolean;
  /**
   * Nulo mientras la cuenta no haya elegido —el alta con Google la crea así—.
   * Para decidir a qué panel entra, pasalo por `normalizeAccountRole`.
   */
  accountRole?: AccountRole | null;
}

/** El rol de quien tiene la sesión abierta, listo para decidir el panel. */
export function currentAccountRole(): AccountRole {
  return normalizeAccountRole(getUser()?.accountRole);
}

export function getToken(): string | null {
  return readSessionToken();
}

export function getUser(): SessionUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as SessionUser) : null;
  } catch {
    return null;
  }
}

export function isSignedIn(): boolean {
  return getToken() !== null;
}

/**
 * Vuelve a leer del servidor quién está usando el panel.
 *
 * La copia de `dd-user` se escribe una sola vez, al ingresar, y desde entonces
 * queda congelada: si el nombre o el correo cambian —los cambia quien
 * administra, o la propia persona— el encabezado sigue mostrando los de
 * cuando abrió sesión, y encima entre recargas, porque vive en el navegador.
 * Devuelve `null` si la sesión ya no vale, sin borrarla: de eso se encarga el
 * 401 del cliente de la API, que además avisa.
 */
export async function fetchSessionUser(): Promise<SessionUser | null> {
  const token = getToken();
  if (!token) return null;
  const res = await fetch(`${API_URL}/api/auth/get-session`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  const data = (await res.json().catch(() => null)) as {
    user?: SessionUser;
  } | null;
  if (!data?.user) return null;
  localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  rememberAccountRole(normalizeAccountRole(data.user.accountRole));
  return data.user;
}

export function signOut(): void {
  writeSessionToken(null);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem("dd-organization-id");
  localStorage.removeItem("dd-location-id");
  // Si no, la pantalla de ingreso le sigue hablando a quien ya se fue.
  forgetAccountRole();
}

async function privateAuthRequest(
  path: string,
  body: Record<string, unknown>,
): Promise<unknown> {
  const token = getToken();
  if (!token) throw new Error("Tu sesión venció. Volvé a ingresar.");
  const res = await fetch(`${API_URL}/api/auth/${path}`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${token}`,
      "content-type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => null)) as {
    message?: string;
    user?: SessionUser;
  } | null;
  if (!res.ok) {
    throw new Error(
      translateErrorMessage(data?.message, "No pudimos guardar los cambios."),
    );
  }
  const rotatedToken = res.headers.get("set-auth-token");
  if (rotatedToken) writeSessionToken(rotatedToken);
  return data;
}

export interface AccountSession {
  id: string;
  token: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  ipAddress?: string | null;
  userAgent?: string | null;
  current?: boolean;
}

export async function listSessions(): Promise<AccountSession[]> {
  const token = getToken();
  const request = { authorization: `Bearer ${token}` };
  const [sessionsResponse, currentResponse] = await Promise.all([
    fetch(`${API_URL}/api/auth/list-sessions`, {
      headers: request,
      credentials: "include",
    }),
    fetch(`${API_URL}/api/auth/get-session`, { headers: request }),
  ]);
  if (!sessionsResponse.ok) throw new Error("No pudimos cargar las sesiones.");

  const sessions = (await sessionsResponse.json()) as AccountSession[];
  const current = currentResponse.ok
    ? ((await currentResponse.json().catch(() => null)) as {
        session?: { id?: string; token?: string };
      } | null)
    : null;
  const currentId = current?.session?.id;
  const currentToken = current?.session?.token ?? token;

  return sessions
    .map((session) => ({
      ...session,
      current:
        (Boolean(currentId) && session.id === currentId) ||
        session.token === currentToken,
    }))
    .sort((a, b) => Number(Boolean(b.current)) - Number(Boolean(a.current)));
}

export const revokeSession = (token: string) =>
  privateAuthRequest("revoke-session", { token }).then(() => undefined);
export const revokeOtherSessions = () =>
  privateAuthRequest("revoke-other-sessions", {}).then(() => undefined);
export const enableTwoFactor = (password: string) =>
  privateAuthRequest("two-factor/enable", {
    password,
    issuer: "Ronda",
  }) as Promise<{
    totpURI: string;
    backupCodes: string[];
  }>;
export const confirmTwoFactor = (code: string) =>
  privateAuthRequest("two-factor/verify-totp", {
    code,
    trustDevice: false,
  }).then(() => fetchSessionUser());
export const disableTwoFactor = (password: string) =>
  privateAuthRequest("two-factor/disable", { password }).then(() =>
    fetchSessionUser(),
  );
export const regenerateRecoveryCodes = (password: string) =>
  privateAuthRequest("two-factor/generate-backup-codes", {
    password,
  }) as Promise<{
    backupCodes: string[];
  }>;

export async function updateUserName(name: string): Promise<SessionUser> {
  await privateAuthRequest("update-user", { name });
  const user = await fetchSessionUser();
  if (!user) throw new Error("No pudimos actualizar tu sesión.");
  return user;
}

export async function requestEmailChange(newEmail: string): Promise<void> {
  await privateAuthRequest("change-email", {
    newEmail,
    callbackURL: `${window.location.origin}/settings/profile`,
  });
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await privateAuthRequest("change-password", {
    currentPassword,
    newPassword,
    revokeOtherSessions: true,
  });
}

interface AuthResponse {
  token?: string;
  user?: SessionUser;
  message?: string;
  twoFactorRedirect?: boolean;
  twoFactorMethods?: string[];
}

async function authRequest(
  path: string,
  body: Record<string, string>,
): Promise<SessionUser> {
  const res = await fetch(`${API_URL}/api/auth/${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });

  const data = (await res.json().catch(() => null)) as AuthResponse | null;

  if (!res.ok) {
    throw new Error(
      translateErrorMessage(
        data?.message,
        "No pudimos iniciar sesión. Revisá tus datos.",
      ),
    );
  }

  // The plugin sets `set-auth-token`; the body carries the same value. Prefer
  // the header (it's authoritative) and fall back for older responses.
  const token = res.headers.get("set-auth-token") ?? data?.token;
  if (data?.twoFactorRedirect) {
    throw new TwoFactorRequiredError(data.twoFactorMethods ?? ["totp"]);
  }
  if (!token || !data?.user) {
    throw new Error("El servidor no devolvió una sesión. Intentá nuevamente.");
  }

  writeSessionToken(token);
  localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  return data.user;
}

export class TwoFactorRequiredError extends Error {
  constructor(readonly methods: string[]) {
    super("Ingresá el código de tu autenticador.");
    this.name = "TwoFactorRequiredError";
  }
}

async function completeTwoFactor(path: string, body: Record<string, unknown>) {
  const res = await fetch(`${API_URL}/api/auth/two-factor/${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => null)) as AuthResponse | null;
  if (!res.ok)
    throw new Error(
      translateErrorMessage(data?.message, "El código no es válido."),
    );
  const token = res.headers.get("set-auth-token") ?? data?.token;
  if (!token || !data?.user) throw new Error("No pudimos completar la sesión.");
  writeSessionToken(token);
  localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  return data.user;
}

export const verifySignInTotp = (code: string) =>
  completeTwoFactor("verify-totp", { code, trustDevice: false });
export const verifySignInRecoveryCode = (code: string) =>
  completeTwoFactor("verify-backup-code", { code, trustDevice: false });

async function publicAuthRequest(path: string, body: Record<string, string>) {
  const res = await fetch(`${API_URL}/api/auth/${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => null)) as AuthResponse | null;
  if (!res.ok)
    throw new Error(
      translateErrorMessage(
        data?.message,
        "No pudimos completar la solicitud.",
      ),
    );
}

export function requestPasswordReset(email: string) {
  return publicAuthRequest("request-password-reset", {
    email,
    redirectTo: `${location.origin}/reset-password`,
  });
}

/**
 * Pide el correo de verificación.
 *
 * `callbackURL` vuelve a donde estaba: quien lo pide desde el portal tiene que
 * volver al portal, no al ingreso del consultorio.
 */
export function requestEmailVerification(email: string, backTo = "/login") {
  return publicAuthRequest("send-verification-email", {
    email,
    callbackURL: `${location.origin}${backTo}`,
  });
}

export function resetPassword(token: string, newPassword: string) {
  return publicAuthRequest("reset-password", { token, newPassword });
}

export function signIn(email: string, password: string): Promise<SessionUser> {
  return authRequest("sign-in/email", { email, password });
}

/**
 * El tipo de mensaje que la página de cierre del popup le postea al panel.
 *
 * La constante es de Better Auth (`better-auth/plugins`, `oauth-popup`), pero
 * el panel no depende de ese paquete —el cliente de sesión está escrito a
 * mano acá— así que se copia el valor en vez de arrastrar la librería entera
 * al bundle por una cadena. Si alguna vez deja de coincidir, el ingreso con
 * Google se queda esperando y termina en "se cerró antes de tiempo", que es
 * una falla ruidosa y no una silenciosa.
 */
const OAUTH_POPUP_MESSAGE = "better-auth:oauth-popup";

/** Cinco minutos: lo que tarda alguien en elegir cuenta y escribir su clave. */
const POPUP_TIMEOUT_MS = 5 * 60 * 1000;

interface OAuthPopupMessage {
  type: string;
  nonce: string;
  token?: string;
  error?: { code: string; description?: string };
}

/**
 * Ingreso con Google, en una ventana aparte.
 *
 * Por qué una ventana y no una redirección del panel entero: el panel y la
 * API están en dominios distintos y la sesión vive como *bearer* en el
 * navegador, no como cookie. Una redirección completa vuelve del proveedor
 * con la sesión puesta en una cookie del dominio de la API, que este panel no
 * puede leer sin depender de cookies de terceros. La ventana, en cambio,
 * navega en su propio contexto de primera parte y al terminar le postea el
 * token al panel.
 *
 * `window.open` va primero y sin `await` delante: si se abre después de una
 * promesa, el navegador ya no la considera consecuencia del clic y la bloquea.
 *
 * Nada de esto abre una puerta: el token llega desde el origen de la API
 * —comprobado en cada mensaje— y con un `nonce` que esta llamada acaba de
 * generar, así que una pestaña ajena no puede inyectar una sesión.
 */
export async function signInWithGoogle(): Promise<SessionUser> {
  const nonce = crypto.randomUUID();
  const start = new URL("/api/auth/oauth-popup/start", API_URL);
  start.searchParams.set("provider", "google");
  start.searchParams.set("popupOrigin", location.origin);
  start.searchParams.set("popupNonce", nonce);
  start.searchParams.set("callbackURL", `${location.origin}/`);
  // `noopener=no` explícito: la página de cierre le postea el token a
  // `window.opener`, así que la ventana tiene que conservar la referencia. Es
  // el valor por defecto, pero acá es un requisito y no un descuido.
  const popup = window.open(
    start.toString(),
    "ronda-google",
    "width=500,height=640,noopener=no",
  );
  if (!popup) {
    throw new Error(
      "El navegador bloqueó la ventana de Google. Permitila y probá de nuevo.",
    );
  }

  const apiOrigin = new URL(API_URL, location.origin).origin;
  const token = await new Promise<string>((resolve, reject) => {
    let settled = false;
    const finish = (run: () => void) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      clearInterval(closedTimer);
      clearTimeout(timeout);
      run();
    };

    function onMessage(event: MessageEvent) {
      if (event.origin !== apiOrigin) return;
      const data = event.data as OAuthPopupMessage | null;
      if (data?.type !== OAUTH_POPUP_MESSAGE || data.nonce !== nonce) return;
      if (data.token) return finish(() => resolve(data.token!));
      // La cuenta tiene verificación en dos pasos: Google dijo quién es, pero
      // la sesión no se abre hasta que llegue el código. El error viaja como
      // el mismo `TwoFactorRequiredError` que devuelve el ingreso con
      // contraseña, así que la pantalla muestra el formulario que ya tiene y
      // lo completa contra el mismo endpoint.
      if (data.error?.code === "two_factor_required") {
        return finish(() => reject(new TwoFactorRequiredError(["totp"])));
      }
      finish(() =>
        reject(
          new Error(
            translateErrorMessage(
              data.error?.description,
              "Google no pudo confirmar tu identidad. Probá de nuevo.",
            ),
          ),
        ),
      );
    }

    // Cerrar la ventana es una forma legítima de arrepentirse, y sin esto el
    // botón se quedaba girando cinco minutos.
    const closedTimer = setInterval(() => {
      if (!popup.closed) return;
      finish(() =>
        reject(new Error("Se cerró la ventana antes de terminar el ingreso.")),
      );
    }, 400);
    const timeout = setTimeout(() => {
      popup.close();
      finish(() =>
        reject(
          new Error("La ventana de Google tardó demasiado. Probá otra vez."),
        ),
      );
    }, POPUP_TIMEOUT_MS);
    window.addEventListener("message", onMessage);
  });

  writeSessionToken(token);
  const user = await fetchSessionUser();
  if (!user) {
    // El token llegó pero la API no lo reconoce: mejor quedarse sin sesión que
    // con una que va a fallar en la primera pantalla.
    signOut();
    throw new Error("No pudimos abrir la sesión. Probá de nuevo.");
  }
  return user;
}

/**
 * Ingreso automático para una demo, si el entorno lo pide.
 *
 * `VITE_DEMO_LOGIN=email:contraseña` hace que la aplicación entre sola al
 * cargar. Existe para poder pasar un enlace y que quien lo abra vea el panel
 * sin credenciales en la mano.
 *
 * Dos cosas importantes. La primera: **no** es un modo sin autenticación. Se
 * inicia una sesión real contra la misma API, con las mismas reglas y los
 * mismos permisos; lo único que cambia es quién escribe la contraseña. Los
 * guardias de las rutas y del servidor quedan intactos, así que el día que la
 * variable no esté, todo vuelve a pedir login sin tocar una línea.
 *
 * La segunda: cualquiera que tenga la dirección entra. Con esto puesto, el
 * enlace *es* la credencial. Va en `.env` de desarrollo y no puede llegar a un
 * despliegue con datos de pacientes reales.
 *
 * Pide sesión nueva **aunque ya haya un token guardado**. La primera versión
 * salía temprano si encontraba uno, y con eso no entraba: un navegador que
 * había usado la app antes traía el token de una sesión vieja, la API lo
 * rechazaba, el 401 lo borraba y la pantalla terminaba en el ingreso. Un token
 * viejo no es una sesión, y acá cuesta menos pedir una nueva que averiguar si
 * la que hay sirve.
 */
export function demoLoginConfigured(): boolean {
  const credentials = import.meta.env.VITE_DEMO_LOGIN;
  return typeof credentials === "string" && credentials.indexOf(":") > 0;
}

const LOGIN_PREVIEW_KEY = "dd-login-preview";

/**
 * `/login?ver=1`: la pantalla de ingreso, aunque la demo esté puesta.
 *
 * Con el ingreso automático activo esa pantalla no se puede mirar. El arranque
 * abre sesión antes de montar la aplicación y el guardia de la ruta manda al
 * panel, así que revisarla obligaba a apagar la variable de entorno, reiniciar
 * Vite y volver a encenderla después — cada vez.
 *
 * La decisión queda guardada y no vive sólo en la dirección. Con el parámetro
 * solo, cualquier redirección reescribía la barra —basta una vuelta por el
 * panel— y la siguiente visita a `/login` volvía a rebotar: había que reponer
 * el `?ver=1` a mano cada vez. Se pide una vez por navegador y se sostiene;
 * `?ver=0` lo apaga.
 *
 * Sólo decide en `/login`. En el resto de las rutas la demo entra como siempre,
 * así que el enlace de siempre sigue llevando al panel sin escalas.
 *
 * No abre ninguna puerta: lo único que hace es **no** crear una sesión. Detrás
 * del formulario sigue haciendo falta una contraseña, y la API autoriza cada
 * pedido por su cuenta. Es estrictamente menos acceso que el enlace de la demo,
 * no más.
 */
function loginPreviewFlag(): boolean {
  // `globalThis.location` y no `location`: un test de node que sólo quiere una
  // función pura de este módulo no tiene por qué arrastrar un navegador.
  const url = globalThis.location;
  if (!url) return false;
  const store = globalThis.localStorage;
  const asked = new URLSearchParams(url.search).get("ver");
  if (asked === "0") store?.removeItem(LOGIN_PREVIEW_KEY);
  else if (asked === "1") store?.setItem(LOGIN_PREVIEW_KEY, "1");
  return store?.getItem(LOGIN_PREVIEW_KEY) === "1";
}

/**
 * Con la vista puesta, el arranque no abre sesión sola en ninguna ruta.
 *
 * Vale en todas y no sólo en `/login` porque de eso depende poder recorrer lo
 * que viene después del formulario. Quien crea una cuenta nueva para ver el
 * alta de la clínica queda con su sesión recién hecha; si el arranque siguiera
 * entrando solo, la primera recarga la cambiaría por la de la demo y lo
 * devolvería al panel a mitad de camino.
 *
 * El guardia de las rutas privadas sigue pidiendo la sesión de la demo cuando
 * no hay ninguna, así que el enlace de siempre entra al panel igual.
 */
export function loginPreviewEnabled(): boolean {
  return loginPreviewFlag();
}

/** En `/login`, además, se muestra el formulario aunque ya haya sesión. */
export function loginPreviewRequested(): boolean {
  return loginPreviewFlag() && globalThis.location?.pathname === "/login";
}

export async function demoSignIn(): Promise<boolean> {
  const credentials = import.meta.env.VITE_DEMO_LOGIN;
  if (!demoLoginConfigured()) return false;
  // Por índice y no por `split`: una contraseña puede llevar dos puntos.
  const separator = credentials!.indexOf(":");
  try {
    await signIn(
      credentials!.slice(0, separator),
      credentials!.slice(separator + 1),
    );
    return true;
  } catch {
    // Si falla, la aplicación sigue su curso y muestra la pantalla de ingreso:
    // una demo que no puede entrar sola no tiene por qué quedarse en blanco.
    return false;
  }
}

/**
 * Crea la cuenta, con su rol adentro.
 *
 * El rol viaja en el mismo pedido que la crea y no en uno posterior: si fuera
 * una segunda llamada, una red que se corta en el medio dejaría una cuenta sin
 * decidir, y quien eligió "soy paciente" entraría al panel del consultorio.
 */
export function signUp(
  name: string,
  email: string,
  password: string,
  accountRole: AccountRole,
  phone: string,
  licenseNumber?: string,
): Promise<SessionUser> {
  return authRequest("sign-up/email", {
    name,
    email,
    password,
    accountRole,
    phone,
    // Sólo cuando hay: a una paciente no se le pide, y el campo no viaja.
    ...(licenseNumber ? { licenseNumber } : {}),
  });
}
